import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { LogIn, Volume2 } from "lucide-react";

import ChatAnimations from "@/components/chat/ChatAnimations";
import GraduationCard from "@/components/chat/GraduationCard";
import BreakCard from "@/components/chat/BreakCard";
import ActionItemsCard, { parseActionItems } from "@/components/chat/ActionItemsCard";

/**
 * ChatMessages — scrollable area of the Papaya chat window.
 * Extracted from PapayaChat in iter-37.
 *
 * Renders, in order:
 *  - Resplendent-Safari sign-in prompt (when no user)
 *  - All messages (regular bubbles + blueberry-award system cards)
 *  - Per-message replay button + ActionItemsCard for assistant messages
 *  - ChatAnimations overlay (blueberry/carrot pops)
 *  - Daily Oracle (first message + first-ever gold banner)
 *  - BreakCard (integration-pause UI from iter-36)
 *  - GraduationCard (tier complete UI)
 *  - Loading dots
 *
 * messagesEndRef is forwarded in as a prop so the parent's scroll-to-bottom
 * effect keeps working without coupling the two components.
 */
const PAPAYA_AVATAR = "https://customer-assets.emergentagent.com/job_scent-select-bunny/artifacts/kq5buvwj_3acdc1ca-655b-474d-9a20-97c4a3a5100a.png";

const PapayaReplayButton = ({ onClick, index }) => (
  <button
    type="button"
    onClick={onClick}
    className="absolute -bottom-2 -right-2 w-7 h-7 rounded-full flex items-center justify-center shadow-md transition opacity-70 hover:opacity-100 hover:scale-110"
    style={{ background: "linear-gradient(135deg, #4F378B, #7B5EA7)", color: "#FFF" }}
    title="Hear Papaya speak this"
    aria-label="Replay Papaya's voice for this message"
    data-testid={`replay-tts-${index}`}
  >
    <Volume2 size={12} />
  </button>
);

const SignInPrompt = () => (
  <motion.div
    initial={{ opacity: 0, y: -8 }}
    animate={{ opacity: 1, y: 0 }}
    className="rounded-2xl p-4 mb-1"
    style={{
      background: "linear-gradient(135deg, #4a7c3f 0%, #355c2c 100%)",
      border: "1px solid rgba(247,231,193,0.3)",
      boxShadow: "0 8px 20px rgba(53, 92, 44, 0.25)",
    }}
    data-testid="safari-signin-prompt"
  >
    <div className="flex items-start gap-2 mb-3">
      <div
        className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 mt-0.5"
        style={{ border: "1.5px solid rgba(247,231,193,0.4)" }}
      >
        <img src={PAPAYA_AVATAR} alt="Papaya" className="w-full h-full object-cover" />
      </div>
      <div className="flex-1">
        <div
          className="text-[10px] font-bold uppercase tracking-widest mb-1.5"
          style={{ color: "#f7e7c1", fontFamily: "'Cinzel', serif" }}
        >
          Papaya whispers
        </div>
        <p
          className="text-xs leading-relaxed italic"
          style={{ color: "#FFF8E7", fontFamily: "'Nunito', sans-serif" }}
        >
          &ldquo;It&rsquo;s a perilous journey to raise awareness, Little Seed. We are going on a Resplendent Safari, sign in so you do not get lost. Our conversations I will keep to myself, you may share as you wish.&rdquo;
        </p>
      </div>
    </div>
    <button
      type="button"
      onClick={() => {
        // Trigger the global sign-in modal by clicking the top-bar Sign In button.
        // Coupling chosen over prop-drilling because this UI is read-only data,
        // not a controlled flow — and the sign-in button is guaranteed to exist
        // when this chat is open.
        const btn = document.querySelector('[data-testid="sign-in-button"]');
        if (btn) btn.click();
      }}
      className="w-full py-2 rounded-full text-xs font-bold flex items-center justify-center gap-2 transition-transform hover:scale-[1.02]"
      style={{
        backgroundColor: "#f7e7c1",
        color: "#355c2c",
        fontFamily: "'Fredoka', sans-serif",
        letterSpacing: "0.06em",
      }}
      data-testid="safari-signin-button"
    >
      <LogIn size={14} />
      Sign In to begin the Resplendent Safari
    </button>
  </motion.div>
);

const BlueberryAwardCard = ({ msg, index }) => (
  <motion.div
    key={msg.timestamp || `blueberry-${index}`}
    initial={{ opacity: 0, scale: 0.92 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ delay: 0.05, type: "spring", damping: 18, stiffness: 240 }}
    className="self-center w-full max-w-[88%] my-1"
    data-testid={`blueberry-award-${index}`}
  >
    <div
      className="px-4 py-3 rounded-2xl text-center"
      style={{
        background: "linear-gradient(135deg, #2E1F5C 0%, #4F378B 50%, #6B4E9F 100%)",
        border: "1px solid rgba(247, 231, 193, 0.4)",
        boxShadow: "0 6px 24px rgba(79, 55, 139, 0.35)",
      }}
    >
      <div
        className="text-[10px] uppercase tracking-[0.18em] mb-1"
        style={{ color: "#f7e7c1", fontFamily: "'Cinzel', serif" }}
      >
        🫐 Blueberry earned
      </div>
      <div className="text-xs leading-relaxed italic" style={{ color: "#FFF8E7", fontFamily: "'Nunito', sans-serif" }}>
        {msg.insight_message
          ? <>Most insightful turn: &ldquo;{msg.insight_message}&rdquo;</>
          : (typeof msg.content === "string" ? msg.content : "")}
      </div>
    </div>
  </motion.div>
);

const DailyOracleBanner = ({ dailyOracle }) => {
  // One-time gold celebratory banner on the user's very first oracle, then
  // a permanent purple "Daily Oracle" card below it.
  const [isFirstEver, setIsFirstEver] = useState(() => !localStorage.getItem("firstOracleShown"));
  useEffect(() => {
    if (isFirstEver) {
      try { localStorage.setItem("firstOracleShown", "true"); }
      catch (e) { console.warn("[oracle] firstOracleShown set failed:", e?.message); }
      setIsFirstEver(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {isFirstEver && (
        <motion.div
          initial={{ opacity: 0, y: -10, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ type: "spring", damping: 16, stiffness: 240 }}
          className="relative mx-1 mb-1 px-4 py-2 rounded-xl text-center overflow-hidden"
          style={{
            background: "linear-gradient(135deg, #D4A574 0%, #F2CC8F 45%, #D4A574 100%)",
            backgroundSize: "200% 200%",
            animation: "oracle-shimmer 3s ease-in-out infinite",
            boxShadow: "0 6px 22px rgba(212,165,116,0.5)",
            border: "1px solid rgba(255, 248, 220, 0.6)",
          }}
          data-testid="first-oracle-banner"
        >
          <div
            className="text-xs font-bold"
            style={{ color: "#3D2A0E", fontFamily: "'Fredoka', sans-serif", letterSpacing: "0.04em" }}
          >
            {"\u2728"} Your first Daily Oracle has arrived {"\u2728"}
          </div>
        </motion.div>
      )}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: isFirstEver ? 0.35 : 0 }}
        className="p-3 rounded-xl mx-1 mb-1"
        style={{
          background: "linear-gradient(135deg, #1A1030, #2D1F50)",
          border: "1px solid rgba(212,165,116,0.2)",
        }}
        data-testid="daily-oracle"
      >
        <div className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: "#D4A574" }}>
          Daily Oracle
        </div>
        <div className="text-xs leading-relaxed" style={{ color: "#E8E0F8", fontFamily: "'Nunito', sans-serif" }}>
          {dailyOracle.message}
        </div>
      </motion.div>
    </>
  );
};

const LoadingDots = ({ ttsLoading }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-start">
    <div className="bg-white rounded-2xl px-4 py-3 shadow-sm">
      <div className="flex gap-1 items-center">
        <motion.div animate={{ scale: [1, 1.3, 1] }} transition={{ repeat: Infinity, duration: 0.7 }}
          className="w-2 h-2 rounded-full" style={{ backgroundColor: "#7B5EA7" }} />
        <motion.div animate={{ scale: [1, 1.3, 1] }} transition={{ repeat: Infinity, duration: 0.7, delay: 0.15 }}
          className="w-2 h-2 rounded-full" style={{ backgroundColor: "#7B5EA7" }} />
        <motion.div animate={{ scale: [1, 1.3, 1] }} transition={{ repeat: Infinity, duration: 0.7, delay: 0.3 }}
          className="w-2 h-2 rounded-full" style={{ backgroundColor: "#7B5EA7" }} />
        {ttsLoading && <span className="ml-2 text-xs" style={{ color: "#8B7DA8" }}>speaking...</span>}
      </div>
    </div>
  </motion.div>
);

const ChatMessages = ({
  messages, user, sessionId, currentTier, nextTierName,
  isLoading, ttsLoading, dailyOracle,
  // break / graduation state
  showBreakState, blueberries, breakUntil, breakRealization, breakMessage, handleReturn,
  graduationData, setGraduationData,
  // animation flags
  showBlueberryAnim, setShowBlueberryAnim, showCarrotAnim, setShowCarrotAnim,
  // action-item callback
  onTierGraduate,
  // tier-link + tts callbacks (parent owns playback)
  renderWithTierLink, openRoadAhead, unlockAudio, playTTS,
  messagesEndRef,
}) => {
  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ backgroundColor: "#FAF7F2" }}>
      {!user && <SignInPrompt />}

      {messages.map((msg, index) => {
        if (msg.role === "system" && msg.kind === "blueberry-award") {
          return <BlueberryAwardCard key={msg.timestamp || `bb-${index}`} msg={msg} index={index} />;
        }
        const parsed = msg.role === "assistant" ? parseActionItems(msg.content) : null;
        const displayContent = parsed ? parsed.cleanText : msg.content;
        const handleReplay = () => {
          unlockAudio();
          if (msg.tts_audio) playTTS(msg.tts_audio, true);
          else if (displayContent) playTTS(displayContent);
        };
        return (
          <motion.div
            key={msg.timestamp || `msg-${index}`}
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: Math.min(index * 0.03, 0.5) }}
            className={`flex flex-col ${msg.role === "user" ? "items-end" : "items-start"}`}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${msg.role !== "user" ? "shadow-sm relative group" : ""}`}
              style={{
                fontFamily: "'Nunito', sans-serif",
                backgroundColor: msg.role === "user" ? "#4F378B" : "#FFFFFF",
                color: msg.role === "user" ? "#FFFFFF" : "#3D3352",
              }}
            >
              {msg.role === "assistant" && nextTierName && displayContent?.includes(nextTierName)
                ? renderWithTierLink(displayContent, nextTierName, openRoadAhead)
                : displayContent}
              {msg.role === "assistant" && displayContent && (
                <PapayaReplayButton onClick={handleReplay} index={index} />
              )}
            </div>
            {parsed && parsed.items.length > 0 && (
              <div className="w-full max-w-[85%] mt-1">
                <ActionItemsCard
                  items={parsed.items}
                  sessionId={sessionId}
                  currentTier={currentTier}
                  onTierGraduate={onTierGraduate}
                />
              </div>
            )}
          </motion.div>
        );
      })}

      <ChatAnimations
        showBlueberryAnim={showBlueberryAnim} setShowBlueberryAnim={setShowBlueberryAnim}
        showCarrotAnim={showCarrotAnim} setShowCarrotAnim={setShowCarrotAnim}
      />

      {/* Daily Oracle — only on the seeker's first turn into a fresh chat */}
      {dailyOracle && messages.length <= 1 && user && <DailyOracleBanner dailyOracle={dailyOracle} />}

      {/* Break Card — iter-36 integration pause */}
      {showBreakState && !graduationData && (
        <BreakCard
          blueberries={blueberries}
          breakUntil={breakUntil}
          breakRealization={breakRealization}
          earlyReturnMessage={breakMessage}
          handleReturn={handleReturn}
        />
      )}

      <GraduationCard
        graduationData={graduationData}
        setGraduationData={setGraduationData}
        handleReturn={handleReturn}
        sessionId={sessionId}
      />

      {isLoading && <LoadingDots ttsLoading={ttsLoading} />}
      <div ref={messagesEndRef} />
    </div>
  );
};

export default ChatMessages;
